import { useState, useEffect, type ChangeEvent } from "react";
import { useLocation } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Box,
  IconButton,
  Dialog,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getDisasterManualTriggers } from "../../../../../api";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../../components/SnackBarCustom";
import { formatDate } from "../../../../../utils/deteUtils";
import type { DisasterManualTriggerItem } from "../../../../../types/disaster";

type ManualDisasterListProps = {
  isUpdated?: SnackbarData;
};

type ManualDisasterColumn = {
  id: keyof DisasterManualTriggerItem | "name";
  label: string;
  minWidth: number;
  align?: "left" | "right" | "center";
  isSmallScreen: boolean;
  disaster?: boolean;
  m_division?: boolean;
  create_user?: boolean;
};

const columns: ManualDisasterColumn[] = [
  { id: "pref_name", label: "都道府県", minWidth: 136, isSmallScreen: true },
  {
    id: "city_name",
    label: "地区町村",
    minWidth: 80,
    isSmallScreen: true,
    m_division: true,
  },
  {
    id: "name",
    label: "対象災害",
    minWidth: 112,
    align: "left",
    isSmallScreen: true,
    disaster: true,
  },
  {
    id: "site_count",
    label: "対象SS",
    minWidth: 240,
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "update_at",
    label: "登録日時",
    minWidth: 240,
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "create_user",
    label: "登録者",
    minWidth: 172,
    align: "left",
    isSmallScreen: false,
    create_user: true,
  },
];

const renderCellValue = (
  column: ManualDisasterColumn,
  disaster: DisasterManualTriggerItem
): string | number | undefined => {
  if (column.disaster) {
    const value = disaster.disaster?.name;
    return typeof value === "string" || typeof value === "number" ? value : undefined;
  }
  if (column.m_division) {
    const value = disaster.m_division?.city_name;
    return typeof value === "string" || typeof value === "number" ? value : undefined;
  }
  if (column.id === "update_at") {
    return formatDate(disaster.update_at);
  }
  if (column.create_user) {
    return disaster.create_user?.name;
  }
  const value = disaster[column.id as keyof DisasterManualTriggerItem];
  return typeof value === "string" || typeof value === "number" ? value : undefined;
};

export default function ManualDisasterList({
  isUpdated,
}: ManualDisasterListProps) {
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [selectedRow, setSelectedRow] =
    useState<DisasterManualTriggerItem | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [disasters, setDisasters] = useState<DisasterManualTriggerItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const getDisasterManual = async () => {
    try {
      const response = await getDisasterManualTriggers({
        disaster_id: disasterId,
        attach_status: [0],
        order: null,
        sort: null,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        return;
      }
      setDisasters(response.disaster_manual_triggers);
      setTotalCount(response.total_count ?? 0);
      setSnackBarstatus(response);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    getDisasterManual();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page, isUpdated]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    color: "outline.dark",
                    fontWeight: "bold",
                    borderBottom: 2,
                    padding: "8px",
                    "@media screen and (max-width:900px)": {
                      display:
                        column.isSmallScreen === true ? "table-cell" : "none",
                    },
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {disasters.map((disaster) => (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={disaster.id}
                onClick={() => {
                  setSelectedRow(disaster);
                  setIsPopoverOpen(true);
                }}
                sx={{
                  pointerEvents: "none",
                  "@media screen and (max-width:900px)": {
                    cursor: "pointer",
                    pointerEvents: "auto",
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={`${disaster.id}_${String(column.id)}`}
                    align={column.align}
                    sx={{
                      padding: "8px",
                      "@media screen and (max-width:900px)": {
                        display:
                          column.isSmallScreen === true
                            ? "table-cell"
                            : "none",
                      },
                    }}
                  >
                    {renderCellValue(column, disaster)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={isPopoverOpen} onClose={() => setIsPopoverOpen(false)}>
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          }}
        >
          <Box sx={{ paddingLeft: "8px", fontWeight: "bold", fontSize: "20px" }}>
            対応中災害詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setIsPopoverOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {selectedRow &&
              columns.map((column) => (
                <TableRow role="checkbox" tabIndex={-1} key={String(column.id)}>
                  <TableCell
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      boxSizing: "border-box",
                      width: "calc((100vw - 64px) / 2)",
                    }}
                  >
                    {column.label}
                  </TableCell>
                  <TableCell
                    sx={{
                      writingMode: "horizontal-tb",
                      boxSizing: "border-box",
                      width: "calc((100vw - 64px) / 2)",
                      overflowWrap: "break-word",
                    }}
                  >
                    {renderCellValue(column, selectedRow)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Dialog>

      <TablePagination
        rowsPerPageOptions={[10, 30, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
          setRowsPerPage(+event.target.value);
          setPage(0);
        }}
        sx={{
          ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions": {
            margin: "0 !important",
            padding: "0 !important",
          },
        }}
        labelRowsPerPage="表示件数:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`
        }
      />
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
    </Paper>
  );
}
